import type { ThemeName } from '@daynote/shared-types';

export const DEFAULT_BACKGROUND =
  'https://images.unsplash.com/photo-1609205807107-e8ec2120f9de?w=1600&q=80';

export interface ThemeConfig {
  id: ThemeName;
  label: string;
  premium?: boolean;
  vars: Record<string, string>;
}

export const THEMES: ThemeConfig[] = [
  {
    id: 'warm-wood',
    label: '暖木',
    vars: {
      '--bg-primary': '#2a2520',
      '--text-primary': '#f5f1e8',
      '--text-muted': '#b8aa95',
      '--text-dim': 'rgba(245, 241, 232, 0.55)',
      '--accent-today': '#d4b97a',
      '--accent-holiday': '#e89055',
      '--accent-rest': '#6db580',
      '--accent-work': '#c25e5e',
      '--panel-bg': 'rgba(18, 14, 10, 0.45)',
      '--grid-line': 'rgba(255, 255, 255, 0.08)',
      '--bg-gradient': 'linear-gradient(180deg, #3a2f24 0%, #1f1814 100%)',
      '--overlay-gradient':
        'linear-gradient(180deg, rgba(15,10,5,0.55) 0%, rgba(15,10,5,0.25) 50%, rgba(15,10,5,0.55) 100%)',
    },
  },
  {
    id: 'dark',
    label: '深色',
    vars: {
      '--bg-primary': '#1a1a1a',
      '--text-primary': '#e8e8e8',
      '--text-muted': '#9a9a9a',
      '--text-dim': 'rgba(232, 232, 232, 0.5)',
      '--accent-today': '#a0c4ff',
      '--accent-holiday': '#ffb347',
      '--accent-rest': '#6db580',
      '--accent-work': '#e57373',
      '--panel-bg': 'rgba(20, 20, 20, 0.6)',
      '--grid-line': 'rgba(255, 255, 255, 0.06)',
      '--bg-gradient': 'linear-gradient(180deg, #2a2a2a 0%, #111111 100%)',
      '--overlay-gradient':
        'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.6) 100%)',
    },
  },
  {
    id: 'paper',
    label: '纸白',
    premium: true,
    vars: {
      '--bg-primary': '#f5f0e8',
      '--text-primary': '#2a2520',
      '--text-muted': '#7a6e5e',
      '--text-dim': 'rgba(42, 37, 32, 0.5)',
      '--accent-today': '#c49a3c',
      '--accent-holiday': '#d4652a',
      '--accent-rest': '#4a9e6a',
      '--accent-work': '#c25e5e',
      '--panel-bg': 'rgba(255, 255, 255, 0.75)',
      '--grid-line': 'rgba(42, 37, 32, 0.1)',
      '--bg-gradient': 'linear-gradient(180deg, #faf6ef 0%, #ebe4d8 100%)',
      '--overlay-gradient': 'linear-gradient(180deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1))',
    },
  },
  {
    id: 'midnight',
    label: '午夜',
    premium: true,
    vars: {
      '--bg-primary': '#0d1117',
      '--text-primary': '#c9d1d9',
      '--text-muted': '#8b949e',
      '--text-dim': 'rgba(201, 209, 217, 0.45)',
      '--accent-today': '#79c0ff',
      '--accent-holiday': '#ffa657',
      '--accent-rest': '#56d364',
      '--accent-work': '#f85149',
      '--panel-bg': 'rgba(13, 17, 23, 0.85)',
      '--grid-line': 'rgba(255, 255, 255, 0.06)',
      '--bg-gradient': 'linear-gradient(180deg, #161b22 0%, #0d1117 100%)',
      '--overlay-gradient':
        'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)',
    },
  },
];

export function getThemeConfig(theme: ThemeName): ThemeConfig {
  if (theme === 'auto') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return getThemeConfig(prefersDark ? 'dark' : 'warm-wood');
  }
  return THEMES.find((t) => t.id === theme) ?? THEMES[0]!;
}

export function applyTheme(theme: ThemeName, backgroundUrl?: string | null) {
  const config = getThemeConfig(theme);
  const root = document.documentElement;
  Object.entries(config.vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  root.dataset.theme = theme;

  const bg = backgroundUrl || DEFAULT_BACKGROUND;
  document.body.style.backgroundImage = `url('${bg}'), var(--bg-gradient)`;
  document.body.style.backgroundSize = 'cover, auto';
  document.body.style.backgroundPosition = 'center, center';
  document.body.style.backgroundRepeat = 'no-repeat, no-repeat';
  document.body.style.backgroundAttachment = 'fixed, fixed';
  document.body.style.backgroundBlendMode = 'multiply, normal';
}
